import {
  buildGroundedMetaDescription,
  buildGroundedSeoTitle,
  collectGroundedKeywordCandidates,
  metaDescriptionNeedsRegeneration,
  normalizeGroundedKeywords,
  seoTitleNeedsRegeneration,
  type GroundedCopyFacts,
} from "./product-ai-copy-quality";

export interface ProductSeoDraft {
  seoTitle: string;
  metaDescription: string;
  seoKeywords: string;
  urlSlug: string;
}

export type ProductSeoFacts = GroundedCopyFacts;

export function optionGroupsFromVariants(
  variants: Array<{ option_values?: Record<string, string> | null } | null | undefined>,
): Record<string, string[]> {
  const groups = new Map<string, { name: string; values: string[]; seen: Set<string> }>();
  for (const variant of variants) {
    const values = variant?.option_values;
    if (!values || typeof values !== "object") continue;
    for (const [rawName, rawValue] of Object.entries(values)) {
      const name = rawName.trim();
      const value = String(rawValue ?? "").trim();
      if (!name || !value) continue;
      const key = name.toLowerCase();
      const group = groups.get(key) ?? { name, values: [], seen: new Set<string>() };
      const valueKey = value.toLowerCase();
      if (!group.seen.has(valueKey)) {
        group.seen.add(valueKey);
        group.values.push(value);
      }
      groups.set(key, group);
    }
  }
  return Object.fromEntries(Array.from(groups.values()).map((group) => [group.name, group.values]));
}

export function slugifyProductName(name: string): string {
  return String(name ?? "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function groundedKeywordString(
  facts: ProductSeoFacts,
  existing: string[] = [],
): string {
  const candidates = [
    ...existing,
    ...collectGroundedKeywordCandidates(facts),
  ];
  return normalizeGroundedKeywords(candidates, facts).join(", ");
}

export function fillEmptySeoDraft(
  current: ProductSeoDraft,
  facts: ProductSeoFacts,
): ProductSeoDraft {
  return {
    seoTitle: current.seoTitle.trim() || buildGroundedSeoTitle(facts),
    metaDescription:
      current.metaDescription.trim() || buildGroundedMetaDescription(facts),
    seoKeywords:
      current.seoKeywords.trim()
      || groundedKeywordString(facts),
    urlSlug:
      current.urlSlug.trim()
      || slugifyProductName(String(facts.name ?? "")),
  };
}

/**
 * Polish the current canonical SEO fields without replacing operator-authored
 * title/meta copy that is already grounded and structurally sound. Keywords
 * are normalized to grounded, de-duplicated terms. Existing URL slugs are
 * preserved to avoid silent storefront URL changes.
 */
export function improveSeoDraft(
  current: ProductSeoDraft,
  facts: ProductSeoFacts,
): ProductSeoDraft {
  const title = current.seoTitle.trim();
  const meta = current.metaDescription.trim();
  return {
    seoTitle:
      !title || seoTitleNeedsRegeneration(title, facts)
        ? buildGroundedSeoTitle(facts)
        : title,
    metaDescription:
      !meta || metaDescriptionNeedsRegeneration(meta, facts)
        ? buildGroundedMetaDescription(facts)
        : meta,
    // Rebuild keyword terms instead of carrying forward mechanically generated
    // legacy fragments. Title/meta preserve sound operator copy; keywords are
    // cheap to regenerate and are where the old "leather foreway" style
    // fragments accumulated.
    seoKeywords: groundedKeywordString(facts),
    urlSlug:
      current.urlSlug.trim()
      || slugifyProductName(String(facts.name ?? "")),
  };
}

/**
 * Rebuild title/meta/keywords from grounded catalogue facts. A non-empty URL
 * slug is deliberately retained because changing an established slug can
 * break existing links and should be an explicit operator action.
 */
export function regenerateSeoDraft(
  current: ProductSeoDraft,
  facts: ProductSeoFacts,
): ProductSeoDraft {
  return {
    seoTitle: buildGroundedSeoTitle(facts),
    metaDescription: buildGroundedMetaDescription(facts),
    seoKeywords: groundedKeywordString(facts),
    urlSlug:
      current.urlSlug.trim()
      || slugifyProductName(String(facts.name ?? "")),
  };
}
